/**
 * three.js 3D 화면의 문자 라벨 생성
 *
 * 종전에는 TextGeometry + typeface JSON(NanumGothic_*.json, 각 26MB)으로
 * 글자를 입체 지오메트리로 만들었다. 한글 완성형 11,172자를 전부 담느라
 * 폰트 파일만 52MB였고, 그러고도 목록에 없는 음절은 '?'로 그려졌다
 * (three FontLoader.js: data.glyphs[char] || data.glyphs['?']).
 *
 * 라벨에 들어가는 값은 탱크명·기종명처럼 DB에서 오는 임의 문자열이라
 * 폰트를 미리 추려둘 수 없다. 캔버스에 그려 텍스처로 붙이면
 * 브라우저 시스템 폰트를 쓰므로 어떤 문자든 나오고 폰트 파일이 필요 없다.
 *
 * 스프라이트는 항상 카메라를 향하므로 각도에 따라 라벨이 눕지 않는다.
 */
import * as THREE from 'three';

// 캔버스에 그릴 때의 글자 크기(px). 크게 그려 축소해야 확대 시 덜 뭉갠다.
const BASE_PX = 64;
const PADDING = 8;
const FONT_FAMILY = '"Nanum Gothic", "Malgun Gothic", "Apple SD Gothic Neo", sans-serif';

/**
 * 문자열 라벨 스프라이트를 만든다.
 *
 * @param text  라벨 문자열
 * @param color 글자색 (three.js 색상값)
 * @param size  월드 좌표 기준 글자 높이. TextGeometry 의 size 와 같은 의미로 쓴다
 * @returns {THREE.Sprite}
 */
export function createTextSprite(text, {color = 0xFF0000, size = 30} = {}) {
  const str = String(text === null || text === undefined ? '' : text);
  const font = `bold ${BASE_PX}px ${FONT_FAMILY}`;

  // 폭을 재기 위해 한 번, 캔버스 크기를 바꾸면 컨텍스트가 초기화되므로 다시 지정한다
  const canvas = document.createElement('canvas');
  const measureCtx = canvas.getContext('2d');
  measureCtx.font = font;

  const textWidth = Math.max(1, Math.ceil(measureCtx.measureText(str).width));
  canvas.width = textWidth + PADDING * 2;
  canvas.height = BASE_PX + PADDING * 2;

  const ctx = canvas.getContext('2d');
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = `#${new THREE.Color(color).getHexString()}`;
  ctx.fillText(str, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  // 캔버스가 2의 거듭제곱 크기가 아니므로 밉맵을 만들지 않는다
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;

  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({map: texture, transparent: true}));

  // 캔버스 높이(글자+여백)를 월드 크기로 환산해 종전 글자 크기와 맞춘다
  const height = size * (canvas.height / BASE_PX);
  sprite.scale.set(height * (canvas.width / canvas.height), height, 1);

  return sprite;
}
